import { createMemo, For, Show, type Accessor, type JSX } from "solid-js"
import { createStore } from "solid-js/store"
import { A, useNavigate } from "@solidjs/router"
import { base64Encode } from "@openanalyst-ai/core/util/encode"
import { ContextMenu } from "@openanalyst-ai/ui/context-menu"
import { DropdownMenu } from "@openanalyst-ai/ui/dropdown-menu"
import { HoverCard } from "@openanalyst-ai/ui/hover-card"
import { Icon, type IconName } from "@openanalyst-ai/ui/icon"
import { showToast } from "@openanalyst-ai/ui/toast"
import { createSortable } from "@thisbeyond/solid-dnd"
import { useLayout, type LocalProject } from "@/context/layout"
import { useGlobalSync } from "@/context/global-sync"
import { useLanguage } from "@/context/language"
import { useNotification } from "@/context/notification"
import { PluginAppIcon } from "@/plugins/plugin-app-icon"
import { sessionTitle } from "@/utils/session-title"
import { ProjectIcon, type SessionItemProps } from "./sidebar-items"
import { compactSessionTime, sortedRootSessions } from "./helpers"
import { displayPath } from "@/utils/display-path"
import { projectSidebarBadgeKind, projectTileClickAction } from "./sidebar-project-state"
import { sidebarClipboardCopyFailedMessage, sidebarClipboardCopyFailedTitle } from "./sidebar-copy"

export type ProjectSidebarContext = {
  currentDir: Accessor<string>
  currentProject: Accessor<LocalProject | undefined>
  sidebarOpened: Accessor<boolean>
  sidebarHovering: Accessor<boolean>
  selectedProjectNavigates: Accessor<boolean>
  hoverProject: Accessor<string | undefined>
  onProjectMouseEnter: (worktree: string, event: MouseEvent) => void
  onProjectMouseLeave: (worktree: string) => void
  onProjectFocus: (worktree: string) => void
  onHoverOpenChanged: (worktree: string, hovered: boolean) => void
  navigateToProject: (directory: string) => void
  openSidebar: () => void
  closeProject: (directory: string) => void
  showEditProjectDialog: (project: LocalProject) => void
  toggleProjectWorkspaces: (project: LocalProject) => void
  reviewSmartworkAccess: (project: LocalProject) => void
  workspacesEnabled: (project: LocalProject) => boolean
  smartworkCloudEnabled: (project: LocalProject) => boolean
  workspaceIds: (project: LocalProject) => string[]
  workspaceLabel: (directory: string, branch?: string, projectId?: string) => string
  projectLabel: (project: LocalProject) => string
  projectNotificationKind: (project: LocalProject) => "permission" | "response" | "running" | undefined
  openProjectDirectory: (directory: string) => void | Promise<void>
  canOpenProjectDirectory: () => boolean
  sessionProps: Omit<SessionItemProps, "session" | "list" | "slug" | "mobile" | "dense">
}

const copyPath = (language: ReturnType<typeof useLanguage>, value: string, description = value) =>
  navigator.clipboard
    .writeText(value)
    .then(() => {
      showToast({
        variant: "success",
        icon: "circle-check",
        title: language.t("session.share.copy.copied"),
        description,
      })
    })
    .catch((err) => {
      console.error("[layout:sidebar] project path copy failed", err)
      showToast({
        variant: "error",
        title: sidebarClipboardCopyFailedTitle("Project path"),
        description: sidebarClipboardCopyFailedMessage(),
      })
    })

const ProjectPathActionLabel = (props: { icon: IconName; children: JSX.Element }) => (
  <span class="flex min-w-0 items-center gap-2">
    <Icon
      name={props.icon}
      size="small"
      class="shrink-0 text-icon-weak transition-transform duration-150 ease-out motion-reduce:transition-none group-hover/folder-action:translate-x-0.5"
    />
    <span class="truncate">{props.children}</span>
  </span>
)

export const ProjectDragOverlay = (props: {
  projects: Accessor<LocalProject[]>
  activeProject: Accessor<string | undefined>
}): JSX.Element => {
  const project = createMemo(() => props.projects().find((p) => p.worktree === props.activeProject()))
  return (
    <Show when={project()} keyed>
      {(p) => (
        <div class="bg-background-base rounded-xl p-1">
          <ProjectIcon project={p} />
        </div>
      )}
    </Show>
  )
}

export const ProjectDropdown = (props: {
  projects: Accessor<LocalProject[]>
  currentProject: Accessor<LocalProject | undefined>
  ctx: ProjectSidebarContext
  openProjectLabel: JSX.Element
  onOpenProject: () => void
  mobile?: boolean
}): JSX.Element => {
  const notification = useNotification()
  const language = useLanguage()
  const selectedWorktree = createMemo(() => props.currentProject()?.worktree ?? "")
  const selectedLabel = createMemo(() => {
    const project = props.currentProject()
    return project ? props.ctx.projectLabel(project) : language.t("command.project.open")
  })
  const currentProjectNotificationKind = createMemo(() => {
    const project = props.currentProject()
    if (!project) return
    return props.ctx.projectNotificationKind(project)
  })
  const projectDirs = (project: LocalProject) => [project.worktree, ...props.ctx.workspaceIds(project)]
  const unseenCount = (project: LocalProject) =>
    projectDirs(project).reduce((total, directory) => total + notification.project.unseenCount(directory), 0)
  const clearProjectNotifications = (project: LocalProject) =>
    projectDirs(project)
      .filter((directory) => notification.project.unseenCount(directory) > 0)
      .forEach((directory) => notification.project.markViewed(directory))
  const badgeKind = (project: LocalProject) =>
    projectSidebarBadgeKind({
      smartworkCloudEnabled: props.ctx.smartworkCloudEnabled(project),
      workspacesEnabled: props.ctx.workspacesEnabled(project),
    })
  const badgeLabel = (kind: ReturnType<typeof badgeKind>) =>
    kind === "smartwork" ? language.t("sidebar.smartwork.badgeTooltip") : language.t("sidebar.workspaces.badgeTooltip")
  const triggerIcon = () =>
    props.currentProject() ? (
      <ProjectIcon
        project={props.currentProject()!}
        notify={!!currentProjectNotificationKind()}
        notifyKind={currentProjectNotificationKind()}
        active
        badgeKind={badgeKind(props.currentProject()!)}
        badgeLabel={badgeLabel(badgeKind(props.currentProject()!))}
      />
    ) : (
      <Icon name="folder" size="large" />
    )

  return (
    <DropdownMenu modal={!props.ctx.sidebarHovering()}>
      <DropdownMenu.Trigger
        as="button"
        type="button"
        data-action="project-dropdown"
        aria-label={selectedLabel()}
        class="flex size-10 items-center justify-center overflow-hidden rounded-md border border-border-base bg-surface-raised-base p-1 shadow-xs-border-base transition-colors hover:bg-surface-raised-base-hover data-[expanded]:bg-surface-raised-base-hover"
      >
        {triggerIcon()}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content class="mt-1 min-w-72">
          <Show when={props.projects().length > 0}>
            <For each={props.projects()}>
              {(project) => {
                const selected = createMemo(() => selectedWorktree() === project.worktree)
                const notificationKind = createMemo(() => props.ctx.projectNotificationKind(project))
                return (
                  <DropdownMenu.Item
                    data-action="project-switch"
                    data-project={base64Encode(project.worktree)}
                    onSelect={() => {
                      props.ctx.navigateToProject(project.worktree)
                    }}
                  >
                    <DropdownMenu.ItemLabel>
                      <div class="flex min-w-0 items-center gap-2">
                        <div class="shrink-0">
                          <ProjectIcon
                            project={project}
                            notify={!!notificationKind()}
                            notifyKind={notificationKind()}
                            active={selected()}
                            badgeKind={badgeKind(project)}
                            badgeLabel={badgeLabel(badgeKind(project))}
                          />
                        </div>
                        <div class="min-w-0 flex-1">
                          <div class="truncate text-13-medium text-text-strong">{props.ctx.projectLabel(project)}</div>
                          <div class="truncate text-11-regular text-text-weak">{displayPath(project.worktree)}</div>
                        </div>
                        <Show when={selected()}>
                          <Icon name="check" size="small" />
                        </Show>
                      </div>
                    </DropdownMenu.ItemLabel>
                  </DropdownMenu.Item>
                )
              }}
            </For>
            <DropdownMenu.Separator />
          </Show>
          <DropdownMenu.Item data-action="project-open" onSelect={props.onOpenProject}>
            <DropdownMenu.ItemLabel>{props.openProjectLabel}</DropdownMenu.ItemLabel>
          </DropdownMenu.Item>
          <Show when={props.currentProject()} keyed>
            {(project) => (
              <>
                <DropdownMenu.Separator />
                <DropdownMenu.Item onSelect={() => props.ctx.showEditProjectDialog(project)}>
                  <DropdownMenu.ItemLabel>{language.t("common.edit")}</DropdownMenu.ItemLabel>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  data-action="project-workspaces-toggle"
                  data-project={base64Encode(project.worktree)}
                  onSelect={() => props.ctx.toggleProjectWorkspaces(project)}
                >
                  <DropdownMenu.ItemLabel>
                    {props.ctx.workspacesEnabled(project)
                      ? language.t("sidebar.workspaces.disable")
                      : language.t("sidebar.workspaces.enable")}
                  </DropdownMenu.ItemLabel>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  data-action="project-smartwork-review"
                  data-project={base64Encode(project.worktree)}
                  class="group/folder-action"
                  onSelect={() => props.ctx.reviewSmartworkAccess(project)}
                >
                  <DropdownMenu.ItemLabel>
                    <ProjectPathActionLabel icon="cloud-upload">
                      {language.t("sidebar.smartwork.reviewAccess")}
                    </ProjectPathActionLabel>
                  </DropdownMenu.ItemLabel>
                </DropdownMenu.Item>
                <Show when={props.ctx.canOpenProjectDirectory()}>
                  <DropdownMenu.Item
                    data-action="project-open-directory"
                    data-project={base64Encode(project.worktree)}
                    class="group/folder-action"
                    onSelect={() => void props.ctx.openProjectDirectory(project.worktree)}
                  >
                    <DropdownMenu.ItemLabel>
                      <ProjectPathActionLabel icon="folder">
                        {language.t("sidebar.project.openDirectory")}
                      </ProjectPathActionLabel>
                    </DropdownMenu.ItemLabel>
                  </DropdownMenu.Item>
                </Show>
                <DropdownMenu.Item
                  data-action="project-copy-directory"
                  data-project={base64Encode(project.worktree)}
                  class="group/folder-action"
                  onSelect={() => void copyPath(language, project.worktree)}
                >
                  <DropdownMenu.ItemLabel>
                    <ProjectPathActionLabel icon="copy">
                      {language.t("sidebar.project.copyDirectory")}
                    </ProjectPathActionLabel>
                  </DropdownMenu.ItemLabel>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  data-action="project-clear-notifications"
                  data-project={base64Encode(project.worktree)}
                  disabled={unseenCount(project) === 0}
                  onSelect={() => clearProjectNotifications(project)}
                >
                  <DropdownMenu.ItemLabel>{language.t("sidebar.project.clearNotifications")}</DropdownMenu.ItemLabel>
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
                <DropdownMenu.Item
                  data-action="project-close-menu"
                  data-project={base64Encode(project.worktree)}
                  onSelect={() => props.ctx.closeProject(project.worktree)}
                >
                  <DropdownMenu.ItemLabel>{language.t("common.close")}</DropdownMenu.ItemLabel>
                </DropdownMenu.Item>
              </>
            )}
          </Show>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu>
  )
}

const ProjectTile = (props: {
  project: LocalProject
  mobile?: boolean
  sidebarHovering: Accessor<boolean>
  selected: Accessor<boolean>
  selectedProjectNavigates: Accessor<boolean>
  active: Accessor<boolean>
  overlay: Accessor<boolean>
  suppressHover: Accessor<boolean>
  dirs: Accessor<string[]>
  onProjectMouseEnter: (worktree: string, event: MouseEvent) => void
  onProjectMouseLeave: (worktree: string) => void
  onProjectFocus: (worktree: string) => void
  navigateToProject: (directory: string) => void
  showEditProjectDialog: (project: LocalProject) => void
  toggleProjectWorkspaces: (project: LocalProject) => void
  reviewSmartworkAccess: (project: LocalProject) => void
  workspacesEnabled: (project: LocalProject) => boolean
  smartworkCloudEnabled: (project: LocalProject) => boolean
  projectNotificationKind: (project: LocalProject) => "permission" | "response" | "running" | undefined
  openProjectDirectory: (directory: string) => void | Promise<void>
  canOpenProjectDirectory: () => boolean
  closeProject: (directory: string) => void
  setMenu: (value: boolean) => void
  setOpen: (value: boolean) => void
  setSuppressHover: (value: boolean) => void
  label: Accessor<string>
  language: ReturnType<typeof useLanguage>
}): JSX.Element => {
  const notification = useNotification()
  const layout = useLayout()
  const unseenCount = createMemo(() =>
    props.dirs().reduce((total, directory) => total + notification.project.unseenCount(directory), 0),
  )
  const notificationKind = createMemo(() => props.projectNotificationKind(props.project))
  const badgeKind = createMemo(() =>
    projectSidebarBadgeKind({
      smartworkCloudEnabled: props.smartworkCloudEnabled(props.project),
      workspacesEnabled: props.workspacesEnabled(props.project),
    }),
  )
  const badgeLabel = createMemo(() =>
    badgeKind() === "smartwork"
      ? props.language.t("sidebar.smartwork.badgeTooltip")
      : props.language.t("sidebar.workspaces.badgeTooltip"),
  )

  const clear = () =>
    props
      .dirs()
      .filter((directory) => notification.project.unseenCount(directory) > 0)
      .forEach((directory) => notification.project.markViewed(directory))

  return (
    <ContextMenu
      modal={!props.sidebarHovering()}
      onOpenChange={(value) => {
        props.setMenu(value)
        props.setSuppressHover(value)
        if (value) props.setOpen(false)
      }}
    >
      <ContextMenu.Trigger
        as="button"
        type="button"
        aria-label={props.label()}
        data-action="project-switch"
        data-project={base64Encode(props.project.worktree)}
        data-sidebar-project-item=""
        data-sidebar-project-selected={props.selected() ? "" : undefined}
        data-sidebar-workspaces-enabled={props.workspacesEnabled(props.project) ? "" : undefined}
        classList={{
          "group/project-tile relative flex size-10 items-center justify-center overflow-visible rounded-md border p-1 cursor-default transition-[background-color,border-color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-strong-base": true,
          "border-border-base bg-surface-raised-base shadow-xs-border-base hover:bg-surface-raised-base-hover":
            props.selected(),
          "border-transparent bg-transparent hover:border-border-weak-base hover:bg-surface-raised-base-hover hover:shadow-xs-border-base":
            !props.selected() && !props.active(),
          "border-border-weak-base bg-surface-raised-base-hover shadow-xs-border-base":
            !props.selected() && props.active(),
        }}
        onPointerDown={(event) => {
          if (event.button === 0 && !event.ctrlKey) {
            props.setOpen(false)
            props.setSuppressHover(true)
            return
          }
          if (!props.overlay()) return
          if (event.button !== 2 && !(event.button === 0 && event.ctrlKey)) return
          props.setOpen(false)
          props.setSuppressHover(true)
          event.preventDefault()
        }}
        onMouseEnter={(event: MouseEvent) => {
          if (!props.overlay()) return
          if (props.suppressHover()) return
          props.onProjectMouseEnter(props.project.worktree, event)
        }}
        onMouseLeave={() => {
          if (props.suppressHover()) props.setSuppressHover(false)
          if (!props.overlay()) return
          props.onProjectMouseLeave(props.project.worktree)
        }}
        onFocus={() => {
          if (!props.overlay()) return
          if (props.suppressHover()) return
          props.onProjectFocus(props.project.worktree)
        }}
        onClick={() => {
          props.setOpen(false)
          if (
            projectTileClickAction({
              selected: props.selected(),
              selectedProjectNavigates: props.selectedProjectNavigates(),
            }) === "toggle-sidebar"
          ) {
            layout.sidebar.toggle()
            return
          }
          props.navigateToProject(props.project.worktree)
        }}
        onBlur={() => props.setOpen(false)}
      >
        <ProjectIcon
          project={props.project}
          notify={!!notificationKind()}
          notifyKind={notificationKind()}
          active={props.selected()}
          badgeKind={badgeKind()}
          badgeLabel={badgeLabel()}
        />
      </ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content>
          <ContextMenu.Item onSelect={() => props.showEditProjectDialog(props.project)}>
            <ContextMenu.ItemLabel>{props.language.t("common.edit")}</ContextMenu.ItemLabel>
          </ContextMenu.Item>
          <ContextMenu.Item
            data-action="project-workspaces-toggle"
            data-project={base64Encode(props.project.worktree)}
            onSelect={() => props.toggleProjectWorkspaces(props.project)}
          >
            <ContextMenu.ItemLabel>
              {props.workspacesEnabled(props.project)
                ? props.language.t("sidebar.workspaces.disable")
                : props.language.t("sidebar.workspaces.enable")}
            </ContextMenu.ItemLabel>
          </ContextMenu.Item>
          <ContextMenu.Item
            data-action="project-smartwork-review"
            data-project={base64Encode(props.project.worktree)}
            class="group/folder-action"
            onSelect={() => props.reviewSmartworkAccess(props.project)}
          >
            <ContextMenu.ItemLabel>
              <ProjectPathActionLabel icon="cloud-upload">
                {props.language.t("sidebar.smartwork.reviewAccess")}
              </ProjectPathActionLabel>
            </ContextMenu.ItemLabel>
          </ContextMenu.Item>
          <ContextMenu.Item
            data-action="project-clear-notifications"
            data-project={base64Encode(props.project.worktree)}
            disabled={unseenCount() === 0}
            onSelect={clear}
          >
            <ContextMenu.ItemLabel>{props.language.t("sidebar.project.clearNotifications")}</ContextMenu.ItemLabel>
          </ContextMenu.Item>
          <Show when={props.canOpenProjectDirectory()}>
            <ContextMenu.Item
              data-action="project-open-directory"
              data-project={base64Encode(props.project.worktree)}
              class="group/folder-action"
              onSelect={() => void props.openProjectDirectory(props.project.worktree)}
            >
              <ContextMenu.ItemLabel>
                <ProjectPathActionLabel icon="folder">
                  {props.language.t("sidebar.project.openDirectory")}
                </ProjectPathActionLabel>
              </ContextMenu.ItemLabel>
            </ContextMenu.Item>
          </Show>
          <ContextMenu.Item
            data-action="project-copy-directory"
            data-project={base64Encode(props.project.worktree)}
            class="group/folder-action"
            onSelect={() => void copyPath(props.language, props.project.worktree)}
          >
            <ContextMenu.ItemLabel>
              <ProjectPathActionLabel icon="copy">
                {props.language.t("sidebar.project.copyDirectory")}
              </ProjectPathActionLabel>
            </ContextMenu.ItemLabel>
          </ContextMenu.Item>
          <ContextMenu.Separator />
          <ContextMenu.Item
            data-action="project-close-menu"
            data-project={base64Encode(props.project.worktree)}
            onSelect={() => props.closeProject(props.project.worktree)}
          >
            <ContextMenu.ItemLabel>{props.language.t("common.close")}</ContextMenu.ItemLabel>
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu>
  )
}

const ProjectPreviewPanel = (props: {
  project: LocalProject
  mobile?: boolean
  selected: Accessor<boolean>
  workspaceEnabled: Accessor<boolean>
  workspaces: Accessor<string[]>
  projectSessions: Accessor<ReturnType<typeof sortedRootSessions>>
  workspaceSessions: (directory: string) => ReturnType<typeof sortedRootSessions>
  ctx: ProjectSidebarContext
  language: ReturnType<typeof useLanguage>
}) => {
  const navigate = useNavigate()
  const previewSessions = createMemo(() => {
    if (!props.workspaceEnabled()) return props.projectSessions().slice(0, 2)
    return props
      .workspaces()
      .flatMap((directory) => props.workspaceSessions(directory).slice(0, 1))
      .slice(0, 2)
  })
  const openProject = () => {
    props.ctx.openSidebar()
    props.ctx.onHoverOpenChanged(props.project.worktree, false)
    if (props.selected() && !props.ctx.selectedProjectNavigates()) return
    props.ctx.navigateToProject(props.project.worktree)
  }
  return (
    <div class="-m-2 flex w-56 flex-col p-1.5">
      <button
        type="button"
        class="group/project-preview-path flex h-11 w-full items-center gap-2 rounded-md px-2 text-left transition-colors duration-150 ease hover:bg-surface-raised-base-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-strong-base motion-reduce:transition-none"
        onClick={openProject}
      >
        <Icon
          name="folder"
          size="small"
          class="shrink-0 text-icon-base transition-transform duration-150 ease-out group-hover/project-preview-path:scale-105 motion-reduce:transition-none"
        />
        <span class="min-w-0 flex-1">
          <span class="block truncate text-13-medium text-text-strong">{props.ctx.projectLabel(props.project)}</span>
          <span
            class="block truncate text-11-regular text-text-weak transition-[color,transform] duration-150 ease-out group-hover/project-preview-path:translate-x-0.5 group-hover/project-preview-path:text-text-base motion-reduce:transition-none"
            title={props.project.worktree}
          >
            {displayPath(props.project.worktree)}
          </span>
        </span>
      </button>
      <Show when={previewSessions().length > 0}>
        <div class="mt-1 flex flex-col gap-0.5">
          <For each={previewSessions()}>
            {(session) => {
              const sessionTime = session.time.updated ?? session.time.created
              const href = `/${base64Encode(session.directory)}/session/${session.id}`
              const close = () => props.ctx.onHoverOpenChanged(props.project.worktree, false)
              const pluginOrigin = createMemo(() => props.ctx.sessionProps.pluginOrigin?.(session))
              return (
                <div class="group/preview-session flex h-7 min-w-0 items-center rounded-md transition-colors duration-150 ease hover:bg-surface-raised-base-hover focus-within:bg-surface-raised-base-hover motion-reduce:transition-none">
                  <A
                    href={href}
                    class="flex h-full min-w-0 flex-1 items-center gap-2 rounded-md px-2 text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-strong-base"
                    onClick={close}
                  >
                    <span class="min-w-0 flex-1 truncate text-13-regular text-text-base">
                      {props.ctx.sessionProps.viewTitle?.(session) ?? sessionTitle(session.title) ?? "Untitled"}
                    </span>
                    <span
                      class="shrink-0 text-12-regular text-text-weak tabular-nums"
                      title={new Date(sessionTime).toLocaleString()}
                    >
                      {compactSessionTime(sessionTime, props.ctx.sessionProps.now?.() ?? Date.now())}
                    </span>
                  </A>
                  <Show when={pluginOrigin()}>
                    {(origin) => (
                      <A
                        href={origin().href}
                        data-session-plugin-origin={origin().id}
                        aria-label={`Open ${origin().title}`}
                        title={`Open ${origin().title}`}
                        class="flex size-6 shrink-0 items-center justify-center rounded-md text-icon-base transition-colors hover:bg-surface-base-hover hover:text-icon-strong focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-strong-base"
                        onClick={(event: MouseEvent) => {
                          event.stopPropagation()
                          close()
                        }}
                      >
                        <PluginAppIcon
                          id={origin().id}
                          icon={origin().icon}
                          title={origin().title}
                          capabilities={origin().capabilities}
                          size="small"
                        />
                      </A>
                    )}
                  </Show>
                  <DropdownMenu modal={false}>
                    <DropdownMenu.Trigger
                      as="button"
                      type="button"
                      data-action="project-preview-session-actions"
                      aria-label={props.language.t("common.moreOptions")}
                      class="mr-0.5 flex size-6 shrink-0 -translate-x-1 items-center justify-center rounded-md text-icon-base opacity-0 transition-[opacity,transform,background-color] duration-150 ease-out hover:bg-surface-base-hover focus:translate-x-0 focus:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-strong-base group-hover/preview-session:translate-x-0 group-hover/preview-session:opacity-100 data-[expanded]:translate-x-0 data-[expanded]:opacity-100 data-[expanded]:bg-surface-base-active motion-reduce:translate-x-0 motion-reduce:transition-none"
                      onClick={(event: MouseEvent) => event.stopPropagation()}
                    >
                      <Icon name="dot-grid" size="small" />
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content class="min-w-44">
                        <DropdownMenu.Item
                          data-action="project-preview-session-open"
                          onSelect={() => {
                            close()
                            navigate(href)
                          }}
                        >
                          <DropdownMenu.ItemLabel>{props.language.t("sidebar.thread.openChat")}</DropdownMenu.ItemLabel>
                        </DropdownMenu.Item>
                        <Show when={props.ctx.sessionProps.forkSession}>
                          <DropdownMenu.Item
                            data-action="project-preview-session-fork"
                            onSelect={() => {
                              close()
                              void props.ctx.sessionProps.forkSession?.(session)
                            }}
                          >
                            <DropdownMenu.ItemLabel>{props.language.t("command.session.fork")}</DropdownMenu.ItemLabel>
                          </DropdownMenu.Item>
                        </Show>
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu>
                </div>
              )
            }}
          </For>
        </div>
      </Show>
      <div class="mt-1 border-t border-border-weak-base pt-1">
        <Show when={props.ctx.canOpenProjectDirectory()}>
          <button
            type="button"
            data-action="project-preview-open-directory"
            class="group/folder-action flex h-7 w-full items-center gap-2 rounded-md px-2 text-left text-12-medium text-text-weak transition-[background-color,color,transform] duration-150 ease hover:bg-surface-raised-base-hover hover:text-text-base active:scale-[0.99] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-strong-base motion-reduce:active:scale-100 motion-reduce:transition-none"
            onClick={() => void props.ctx.openProjectDirectory(props.project.worktree)}
          >
            <Icon
              name="folder"
              size="small"
              class="shrink-0 text-icon-weak transition-transform duration-150 ease-out group-hover/folder-action:translate-x-0.5 motion-reduce:transition-none"
            />
            <span class="truncate">{props.language.t("sidebar.project.openDirectory")}</span>
          </button>
        </Show>
        <button
          type="button"
          data-action="project-preview-copy-directory"
          class="group/folder-action flex h-7 w-full items-center gap-2 rounded-md px-2 text-left text-12-medium text-text-weak transition-[background-color,color,transform] duration-150 ease hover:bg-surface-raised-base-hover hover:text-text-base active:scale-[0.99] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-strong-base motion-reduce:active:scale-100 motion-reduce:transition-none"
          onClick={() => void copyPath(props.language, props.project.worktree)}
        >
          <Icon
            name="copy"
            size="small"
            class="shrink-0 text-icon-weak transition-transform duration-150 ease-out group-hover/folder-action:translate-x-0.5 motion-reduce:transition-none"
          />
          <span class="truncate">{props.language.t("sidebar.project.copyDirectory")}</span>
        </button>
        <button
          type="button"
          class="flex h-7 w-full items-center rounded-md px-2 text-left text-12-medium text-text-weak transition-colors duration-150 ease hover:bg-surface-raised-base-hover hover:text-text-base focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-strong-base motion-reduce:transition-none"
          onClick={openProject}
        >
          {props.language.t("sidebar.project.viewAllSessions")}
        </button>
      </div>
    </div>
  )
}

export const SortableProject = (props: {
  project: LocalProject
  mobile?: boolean
  ctx: ProjectSidebarContext
  sortNow: Accessor<number>
}): JSX.Element => {
  const globalSync = useGlobalSync()
  const language = useLanguage()
  const sortable = createSortable(props.project.worktree)
  const selected = createMemo(() => props.ctx.currentProject()?.worktree === props.project.worktree)
  const workspaces = createMemo(() => props.ctx.workspaceIds(props.project).slice(0, 2))
  const workspaceEnabled = createMemo(() => props.ctx.workspacesEnabled(props.project))
  const dirs = createMemo(() => props.ctx.workspaceIds(props.project))
  const [state, setState] = createStore({
    menu: false,
    suppressHover: false,
  })

  const isHoverProject = () => props.ctx.hoverProject() === props.project.worktree
  const preview = createMemo(() => !props.mobile && props.ctx.sidebarOpened())
  const overlay = createMemo(() => !props.mobile && !props.ctx.sidebarOpened())
  const active = createMemo(() => state.menu || (preview() ? isHoverProject() : overlay() && isHoverProject()))

  const hoverOpen = () => isHoverProject() && preview() && !selected() && !state.menu

  const projectStore = createMemo(() => globalSync.child(props.project.worktree, { bootstrap: false })[0])
  const projectSessions = createMemo(() => sortedRootSessions(projectStore(), props.sortNow()))
  const workspaceSessions = (directory: string) => {
    const [data] = globalSync.child(directory, { bootstrap: false })
    return sortedRootSessions(data, props.sortNow())
  }
  const tile = () => (
    <ProjectTile
      project={props.project}
      mobile={props.mobile}
      sidebarHovering={props.ctx.sidebarHovering}
      selected={selected}
      selectedProjectNavigates={props.ctx.selectedProjectNavigates}
      active={active}
      overlay={overlay}
      suppressHover={() => state.suppressHover}
      dirs={dirs}
      onProjectMouseEnter={props.ctx.onProjectMouseEnter}
      onProjectMouseLeave={props.ctx.onProjectMouseLeave}
      onProjectFocus={props.ctx.onProjectFocus}
      navigateToProject={props.ctx.navigateToProject}
      showEditProjectDialog={props.ctx.showEditProjectDialog}
      toggleProjectWorkspaces={props.ctx.toggleProjectWorkspaces}
      reviewSmartworkAccess={props.ctx.reviewSmartworkAccess}
      workspacesEnabled={props.ctx.workspacesEnabled}
      smartworkCloudEnabled={props.ctx.smartworkCloudEnabled}
      projectNotificationKind={props.ctx.projectNotificationKind}
      openProjectDirectory={props.ctx.openProjectDirectory}
      canOpenProjectDirectory={props.ctx.canOpenProjectDirectory}
      closeProject={props.ctx.closeProject}
      setMenu={(value) => setState("menu", value)}
      setOpen={(value) => props.ctx.onHoverOpenChanged(props.project.worktree, value)}
      setSuppressHover={(value) => setState("suppressHover", value)}
      label={() => props.ctx.projectLabel(props.project)}
      language={language}
    />
  )

  return (
    // @ts-ignore
    <div use:sortable classList={{ "opacity-30": sortable.isActiveDraggable }}>
      <Show when={preview() && !selected()} fallback={tile()}>
        <HoverCard
          open={!state.suppressHover && hoverOpen() && !state.menu}
          openDelay={0}
          closeDelay={0}
          placement="right-start"
          gutter={6}
          trigger={tile()}
          onOpenChange={(value) => {
            if (state.menu) return
            if (value && state.suppressHover) return
            props.ctx.onHoverOpenChanged(props.project.worktree, value)
          }}
        >
          <ProjectPreviewPanel
            project={props.project}
            mobile={props.mobile}
            selected={selected}
            workspaceEnabled={workspaceEnabled}
            workspaces={workspaces}
            projectSessions={projectSessions}
            workspaceSessions={workspaceSessions}
            ctx={props.ctx}
            language={language}
          />
        </HoverCard>
      </Show>
    </div>
  )
}
